import { useCallback, useEffect, useRef, useState } from 'react';
import { useShowAlert } from 'hooks';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import { DateFormats } from 'UI/constants/defaults';
import { UIStatus } from 'UI/constants/status';
import { localTimeFormatter } from 'UI/utils';

const DEFAULT_SAVE_OPTIONS = {
  silent: false
};

const normalizeSaveOptions = (options = {}) => ({
  silent: options.silent === true
});

const mergeSaveOptions = (currentOptions, nextOptions = {}) => {
  const normalizedNextOptions = normalizeSaveOptions(nextOptions);

  if (!currentOptions) {
    return normalizedNextOptions;
  }

  return {
    silent: currentOptions.silent && normalizedNextOptions.silent
  };
};

const buildFormSnapshot = draftData => {
  const { draftId: _draftId, draftUpdatedAt: _draftUpdatedAt, ...formSnapshot } = draftData || {};

  return formSnapshot;
};

export const useDrafts = ({ initialDraft, entity, endpoint }) => {
  const { showAlert } = useShowAlert();
  const isConfigured = Boolean(entity && endpoint);
  const [draft, setDraft] = useState(initialDraft);
  const [uiDraftState, setUiDraftState] = useState({
    status: UIStatus.Default
  });
  const draftRef = useRef(initialDraft);
  const lastSavedSnapshotRef = useRef(null);
  const isSavingDraftRef = useRef(false);
  const isMountedRef = useRef(true);
  const pendingOptionsRef = useRef(null);
  const pendingUpdatesRef = useRef(null);
  const saveCyclePromiseRef = useRef(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  const draftUpdatedAt = draft?.draftUpdatedAt
    ? localTimeFormatter(draft.draftUpdatedAt, DateFormats.SimpleDateTime)
    : null;

  const updateDraft = useCallback(draftUpdates => {
    if (!isMountedRef.current) return;

    setDraft(prev => {
      const nextDraft = { ...prev, ...draftUpdates };
      draftRef.current = nextDraft;

      return nextDraft;
    });
  }, []);

  const persistPendingSaves = useCallback(
    async persistedDraftId => {
      if (!pendingUpdatesRef.current) {
        return persistedDraftId;
      }

      const snapshotOptions = pendingOptionsRef.current || DEFAULT_SAVE_OPTIONS;
      const snapshotUpdates = pendingUpdatesRef.current;
      const currentDraft = draftRef.current;
      const currentDraftId = currentDraft?.draftId;
      const draftData = {
        ...currentDraft,
        ...snapshotUpdates
      };

      pendingUpdatesRef.current = null;
      pendingOptionsRef.current = null;

      if (isMountedRef.current) {
        setUiDraftState(prev => ({ ...prev, status: UIStatus.Saving }));
      }

      /* TODO on next PR:
      Fix: Either (a) accept a buildBody callback prop so each entity provides its own mapping, 
      or (b) remove the explicit field aliases and rely solely on ...draftData 
      if the backend no longer requires the name/email top-level aliases.
      */
      const body = {
        entity,
        ...draftData,
        name: draftData.firstName,
        lastName: draftData.lastName,
        email: draftData.email,
        companyName: draftData.company?.name,
        companyId: draftData.company?.id
      };
      const response = currentDraftId
        ? await SECONDARY_API_CLIENT.patch(`${endpoint}/${currentDraftId}`, body)
        : await SECONDARY_API_CLIENT.post(endpoint, body);
      const nextPersistedDraftId = currentDraftId || response.data.id;

      updateDraft({
        draftId: nextPersistedDraftId,
        draftUpdatedAt: new Date().toISOString()
      });

      lastSavedSnapshotRef.current = buildFormSnapshot(draftData);

      if (!snapshotOptions.silent) {
        showAlert({
          title: strings.drafts.alert.success.save.title
        });
      }
      if (isMountedRef.current) {
        setUiDraftState(prev => ({ ...prev, status: UIStatus.Success }));
      }

      return persistPendingSaves(nextPersistedDraftId);
    },
    [entity, endpoint, showAlert, updateDraft]
  );

  const saveDraft = useCallback(
    async (updates = {}, options = {}) => {
      if (!isConfigured) {
        return Promise.resolve(null);
      }

      draftRef.current = {
        ...draftRef.current,
        ...updates
      };
      pendingUpdatesRef.current = pendingUpdatesRef.current
        ? {
            ...pendingUpdatesRef.current,
            ...updates
          }
        : { ...updates };
      pendingOptionsRef.current = mergeSaveOptions(pendingOptionsRef.current, options);

      if (isSavingDraftRef.current) {
        return saveCyclePromiseRef.current;
      }

      isSavingDraftRef.current = true;
      saveCyclePromiseRef.current = persistPendingSaves(draftRef.current?.draftId || null)
        .catch(error => {
          if (isMountedRef.current) {
            setUiDraftState(prev => ({ ...prev, status: UIStatus.Error }));
          }
          showAlert({
            title: strings.shared.ui.error,
            error
          });
          return null;
        })
        .finally(() => {
          isSavingDraftRef.current = false;
          pendingOptionsRef.current = null;
          pendingUpdatesRef.current = null;
          saveCyclePromiseRef.current = null;
        });

      return saveCyclePromiseRef.current;
    },
    [isConfigured, persistPendingSaves, showAlert]
  );

  const deleteDraftById = async draftId => {
    if (!isConfigured) {
      return;
    }

    try {
      await SECONDARY_API_CLIENT.delete(`${endpoint}/${draftId}`);
    } catch (error) {
      showAlert({
        title: strings.shared.ui.error,
        error
      });
    }
  };

  return {
    deleteDraftById,
    draftId: draft?.draftId,
    draftUpdatedAt,
    isSavingDraft: uiDraftState.status === UIStatus.Saving,
    lastSavedSnapshotRef,
    saveDraft,
    updateDraft
  };
};
